import angular from 'angular';
import moment from 'moment';

import './event-date-modal.style.sass';
import template from './event-date-modal.tpl.pug';

const REPEAT_END_TOO_FAR_MESSAGE = 'This repeat schedule ends too far in the future. Choose an earlier date or fewer occurrences.';
const MAX_REPEAT_YEAR = 9999;
const MAX_BACKEND_INTEGER = 2147483647;
const MAX_SAFE_INTEGER = 9007199254740991;

function isSafeInteger(value) {
    return typeof value === 'number'
        && isFinite(value)
        && Math.floor(value) === value
        && Math.abs(value) <= MAX_SAFE_INTEGER;
}

class EventDateController {
    /* @ngInject */
    constructor(arrayUtil, stringUtil, eventUtil, modalService) {
        this.arrayUtil = arrayUtil;
        this.stringUtil = stringUtil;
        this.eventUtil = eventUtil;
        this.modalService = modalService;
    }

    $onInit() {
        this.canRepeat = this.canRepeat !== false;

        this.unitOptions = [
            { id: this.eventUtil.EventRepeatUnit.Daily, value: 'Day' },
            { id: this.eventUtil.EventRepeatUnit.Weekly, value: 'Week' },
            { id: this.eventUtil.EventRepeatUnit.Monthly, value: 'Month' },
            { id: this.eventUtil.EventRepeatUnit.Yearly, value: 'Year' },
        ];

        this.endOptions = [
            { id: this.eventUtil.EventRepeatEndType.Never, value: 'Never' },
            { id: this.eventUtil.EventRepeatEndType.AfterOccurrence, value: 'After' },
            { id: this.eventUtil.EventRepeatEndType.OnDate, value: 'On' }
        ];

        this.timestamp = this.event.timestamp;
        this.repeating = !!this.event.repeat;
        this.previousRepeating = this.repeating;
        this.frequency = this.repeating ? this.event.repeat.frequency : 1;
        this.unit = this.repeating ? this.event.repeat.unit : this.eventUtil.EventRepeatUnit.Monthly;
        this.autoConfirm = this.repeating ? !!this.event.repeat.autoConfirm : false;
        this.endType = this.repeating
            ? this.event.repeat.end
                ? this.event.repeat.end.date
                    ? this.eventUtil.EventRepeatEndType.OnDate
                    : this.eventUtil.EventRepeatEndType.AfterOccurrence
                : this.eventUtil.EventRepeatEndType.Never
            : this.eventUtil.EventRepeatEndType.Never;
        this.endOccurrence = this.willEndAfterOccurrence ? this.event.repeat.end.occurrence : 4;
        this.endDate = this.willEndOnDate ? this.event.repeat.end.date : this.getEndDate(this.timestamp, this.frequency, this.unit, this.endOccurrence);
        this.repeatEndError = this.initialRepeatEndError || null;
        this.repeatStateWhenInvalid = this.getRepeatState();
    }

    $doCheck() {
        if (this.previousRepeating && !this.repeating) {
            this.autoConfirm = false;
        }

        this.previousRepeating = this.repeating;

        if (this.repeatEndError && this.repeatStateWhenInvalid !== this.getRepeatState()) {
            this.repeatEndError = null;
            this.repeatStateWhenInvalid = this.getRepeatState();
        }
    }

    get occurrencePluralized() {
        return this.stringUtil.pluralize(this.endOccurrence, 'occurrence');
    }

    get willEnd() {
        return this.repeating && this.endType !== this.eventUtil.EventRepeatEndType.Never;
    }

    get willEndAfterOccurrence() {
        return this.repeating && this.endType === this.eventUtil.EventRepeatEndType.AfterOccurrence;
    }

    get willEndOnDate() {
        return this.repeating && this.endType === this.eventUtil.EventRepeatEndType.OnDate;
    }

    get summary() {
        let event = this.updateEvent(this.event);

        return [`${this.eventUtil.getRepeatingFrequencySummary(event.repeat)}${this.getRepeatingUnitDetail(event)}`, this.eventUtil.getRepeatingEndSummary(event.repeat)]
            .filter((summary) => summary && summary.length > 0)
            .join(', ');
    }

    getRepeatingUnitDetail(event) {
        let repeat = event.repeat || {};
        let timestamp = event.transactionDate || event.timestamp;

        switch (repeat.unit) {
            case this.eventUtil.EventRepeatUnit.Weekly:
                return ` (on ${moment(timestamp).format('dddd')})`;
            case this.eventUtil.EventRepeatUnit.Monthly:
                return ` (on the ${moment(timestamp).format('Do')})`;
            case this.eventUtil.EventRepeatUnit.Yearly:
                return ` (on ${moment(timestamp).format('MMMM')} the ${moment(timestamp).format('Do')})`;
        }

        return '';
    }

    getDefaultEndOccurrence() {
        return 4;
    }

    getEndDate(timestamp, frequency, unit, endOccurrence) {
        return moment(timestamp).add(frequency * (endOccurrence - 1), unit).valueOf();
    }

    getRepeatState() {
        return [
            this.repeating,
            this.frequency,
            this.unit,
            this.endType,
            this.endOccurrence,
            this.endDate
        ].join('|');
    }

    getRepeatEnd() {
        if (!this.willEnd) {
            return null;
        }

        return this.willEndOnDate
            ? { date: this.endDate }
            : { occurrence: this.endOccurrence };
    }

    validateRepeat() {
        let frequency = Number(this.frequency);
        let isValid = !this.repeating
            || isSafeInteger(frequency) && frequency >= 1 && frequency <= MAX_BACKEND_INTEGER;

        if (isValid && this.willEndOnDate) {
            let endDate = moment(this.endDate);
            isValid = endDate.isValid() && endDate.year() >= 1 && endDate.year() <= MAX_REPEAT_YEAR;
        }

        if (isValid && this.willEndAfterOccurrence) {
            let occurrence = Number(this.endOccurrence);
            let intervals = frequency * (occurrence - 1);
            let finalOccurrence = moment(this.timestamp).add(intervals, this.unit);

            isValid = isSafeInteger(occurrence)
                && occurrence >= 1
                && occurrence <= MAX_BACKEND_INTEGER
                && isSafeInteger(intervals)
                && intervals <= MAX_BACKEND_INTEGER
                && finalOccurrence.isValid()
                && finalOccurrence.year() >= 1
                && finalOccurrence.year() <= MAX_REPEAT_YEAR;
        }

        if (!isValid) {
            this.repeatEndError = REPEAT_END_TOO_FAR_MESSAGE;
            this.repeatStateWhenInvalid = this.getRepeatState();
        }

        return isValid;
    }

    updateEvent(event) {
        let updatedEvent = angular.merge({}, event, {
            timestamp: this.timestamp,
            transactionDate: moment(this.timestamp).format('YYYY-MM-DD'),
            repeat: this.repeating
                ? {
                    frequency: this.frequency,
                    unit: this.unit,
                    autoConfirm: !!this.autoConfirm,
                    end: this.getRepeatEnd()
                }
                : null
        });

        if (updatedEvent.repeat) {
            updatedEvent.repeat.end = this.getRepeatEnd();
        }

        return updatedEvent;
    }

    selectDate() {
        let endDate = this.willEndOnDate ? this.event.repeat.end.date : this.getEndDate(this.timestamp, this.frequency, this.unit, this.endOccurrence);

        return this.modalService.open({
            component: 'dbADateModal',
            bindings: {
                title: 'Date',
                startOfWeek: this.startOfWeek,
                timestamp: this.timestamp,
                onDayCheckDisabled: (timestamp) => timestamp > endDate
            }
        }).then((timestamp) => {
            this.timestamp = timestamp;
        }).catch(() => null);
    }

    selectEndDate() {
        return this.modalService.open({
            component: 'dbADateModal',
            bindings: {
                title: 'Repeat ends on',
                startOfWeek: this.startOfWeek,
                timestamp: this.endDate,
                onDayCheckDisabled: (timestamp) => timestamp < this.timestamp || moment(timestamp).year() > MAX_REPEAT_YEAR
            }
        }).then((timestamp) => {
            this.endDate = timestamp;
        }).catch(() => null);
    }

    dismiss() {
        return this.onDismiss();
    }

    close() {
        if (!this.validateRepeat()) {
            return null;
        }

        return this.onClose({
            data: this.updateEvent(this.event)
        });
    }
}

export default {
    selector: 'dbAEventDateModal',
    template: template(),
    controller: EventDateController,
    bindings: {
        event: '<',
        startOfWeek: '<',
        canRepeat: '<',
        initialRepeatEndError: '<',

        canEdit: '<',

        onClose: '&',
        onDismiss: '&'
    }
};



// WEBPACK FOOTER //
// ./src/application/component/modals/component/event-date-modal/event-date-modal.cmpt.js